import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Centralized SEO head for industry landing pages.
 *
 * Emits per-page:
 *  - <title> and meta description (locale-specific)
 *  - canonical + hreflang alternates (en / es / tr / x-default)
 *  - Open Graph + Twitter Card tags
 *  - JSON-LD: Service schema with $149 Offer + 60-day refund policy
 *  - JSON-LD: FAQPage schema (rich snippets in Google search) when faqItems provided
 *
 * Convention: `path` is the canonical English path (e.g. "/dental").
 * Spanish/Turkish alternates are derived as "/es/dental" and "/tr/dental".
 */
// Must match the domain Vercel actually serves. Apex aiworkshopllc.com
// 307-redirects to www.aiworkshopllc.com — if canonicals point to the
// redirecting apex version, Google flags "Duplicate without user-
// selected canonical" and refuses to index.
const SITE = "https://www.aiworkshopllc.com";

const OG_LOCALE = { en: "en_US", es: "es_ES", tr: "tr_TR" };

export default function SEOHead({
  path,           // canonical EN path, e.g. "/dental"
  lang = "en",    // active language for the rendered page
  title,          // <title> + og:title + twitter:title
  description,    // meta description + og + twitter
  faqItems,       // optional [{ q, a }] for FAQPage rich snippets
  price = 149,
  industry,       // free-form industry label, used in Service schema
}) {
  const enUrl = `${SITE}${path}`;
  const esUrl = `${SITE}/es${path}`;
  const trUrl = `${SITE}/tr${path}`;
  const currentUrl = lang === "es" ? esUrl : lang === "tr" ? trUrl : enUrl;

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: title,
    description,
    url: currentUrl,
    provider: {
      "@type": "Organization",
      name: "AI Workshop LLC",
      url: SITE,
    },
    areaServed: industry || "Small business",
    offers: {
      "@type": "Offer",
      price: String(price),
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url: currentUrl,
    },
    hasMerchantReturnPolicy: {
      "@type": "MerchantReturnPolicy",
      applicableCountry: "US",
      returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
      merchantReturnDays: 60,
      returnMethod: "https://schema.org/ReturnByMail",
      returnFees: "https://schema.org/FreeReturn",
    },
  };

  const faqSchema = faqItems && faqItems.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  } : null;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />

      <link rel="canonical" href={currentUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="es" href={esUrl} />
      <link rel="alternate" hrefLang="tr" href={trUrl} />
      <link rel="alternate" hrefLang="x-default" href={enUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={`${SITE}/android-chrome-512x512.png`} />
      <meta property="og:locale" content={OG_LOCALE[lang] || OG_LOCALE.en} />
      <meta property="og:site_name" content="AI Workshop LLC" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${SITE}/android-chrome-512x512.png`} />

      <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
    </Helmet>
  );
}
